<template>
  <form class="cart__form" action="/checkout" method="post">
    <CartHead
      :isEmpty="isEmpty"
      :cartData="cartData"
      :cartTotal="cartTotal"
      :hasFreeShipping="hasFreeShipping"
      :hasFreeGift="hasFreeGift"
      :freeShippingMin="freeShippingMin"
      :freeGiftMin="freeGiftMin"
      @toggle-cart="toggleCart"
    />
    <div class="cart__body">
      <div v-if="!isEmpty"  class="cart__inner">
        <div class="cart__items">
          <CartItem v-if="addingProduct" :placeholder="true" />
          <CartItem
            v-for="(item,index) in items"
            :cart="items"
            :key="item.key"
            :item="item"
          />
          <!-- Beam widget -->
          <div style="margin: 15px 0px 0px;" data-beam-widget="select-nonprofit">
            <beam-select-nonprofit
              apiKey="1h6d2pKq7jSo.e18689b8-a532-4405-b655-08c07b3e67f4"
              :storeId="481"
              lang="en"
            ></beam-select-nonprofit>
          </div>
        </div>
        <CartFooter
          v-if="!isEmpty && !isMobile"
          :cart="cart"
          :cartData="cartData"
          :cartHasSubscriptionProducts="cartHasSubscriptionProducts"
          :hasFreeShipping="hasFreeShipping"
        />
      </div>
      <CartRecs
        v-if="cartRecs.length"
        :cartRecs="cartRecs"
        :items="items"
      />
    </div>

    <CartFooter
      v-if="!isEmpty && isMobile"
      :cart="cart"
      :cartData="cartData"
      :cartHasSubscriptionProducts="cartHasSubscriptionProducts"
      :hasFreeShipping="hasFreeShipping"
    />

    <CartEmptyFooter 
      v-if="isEmpty"
    />
  </form>
</template>

<script setup>
  import { ref, computed, onBeforeMount, onMounted, watch, toRefs } from 'vue';
  import {useStore} from "vuex";
  import cartHelpers from '../api'
  import CartHead from './cart-head.vue';
  import CartItem from './cart-item.vue';
  import CartFooter from './cart-footer.vue';
  import CartRecs from './cart-recs.vue';
  import CartEmptyFooter from './cart-empty-footer.vue';
  import windowResize from '../../../utils/window-resize';

  const props = defineProps({
    cartData: {
      type: Object,
      default: ({})
    },

    isCart: {
      type: Boolean,
      default: false
    },

    isCartOpen: {
      type: Boolean,
      default: false
    },
  })

  const { cartData } = toRefs(props)
  const { windowWidth } = windowResize()
  const { status } = cartHelpers;
  const store = useStore();
  var cbs = ref([])
  const cartRecs = ref([])
  const emit = defineEmits(['on-toggle'])
  
  onBeforeMount(async () => {
    // Setting window function for additem
    window.initReploCart = initReploCart;
    window.Additem = addItem;
    window.updateCartItems = update;
    window.cartItems = items.value;

    // Initialise store init function
    initCart();
    getCartRecs();
  })

  const cart = computed(() => store.state.cart)
  const totalCount = computed(() => store.state.cart.item_count)
  const cartStatus = computed(() => store.state.status)
  const isEmpty = computed(() => store.state.status === status.empty)
  const isMobile = computed(() => { return windowWidth.value < 1024;})

  const items = computed(() => {
    const items = cart.value.items || [];
    return items
    .map((item, index) => ({
      ...item,
      image: item.featured_image,
      line: index + 1,
    }))
  })

  const totalCartItems = computed(() => {
     return totalCount.value;
  })

  const addingProduct = computed(() =>{
    return cartStatus.value === status.adding;
  })

  const cartTotal = computed(() => {
    return (cart.value.total_price)
  })

  const hasFreeShipping = computed(() => {
    if (isGiftCardOnly.value) {
      return true;
    }

    if (cartHasSubscriptionProducts.value) {
      return cartTotal.value >= cartData.value.shippingRates.subscription.minimum;
    }

    return cartTotal.value >= cartData.value.shippingRates.onetime.minimum;
  })

  const hasFreeGift = computed(() => {
    if (cartHasSubscriptionProducts.value) {
      return cartTotal.value >= cartData.value.freeGift.subscription;
    }

    return cartTotal.value >= cartData.value.freeGift.onetime;
  })

  const freeShippingMin = computed(() => {
    if (cartHasSubscriptionProducts.value) {
      return cartData.value.shippingRates.subscription.minimum
    }

    return cartData.value.shippingRates.onetime.minimum;
  })

  const freeGiftMin = computed(() => {
    if (cartHasSubscriptionProducts.value) {
      return cartData.value.freeGift.subscription
    }

    return cartData.value.freeGift.onetime;
  })

  const cartHasSubscriptionProducts = computed(() => {
    return !isEmpty.value && items.value.some(item => item.selling_plan_allocation);
  })

  const isGiftCardOnly = computed(() => {
    return !isEmpty.value && items.value.every(item => item.gift_card === true);
  })

  const initCart = () => {
    store.dispatch("init")
  }

  const initReploCart = () => {
    store.dispatch("initReplo")
  }

  const getCartRecs = async() => {
    let pageUrl = encodeURI(window.location.href);
    await fetch(`https://rebuyengine.com/api/v1/custom/id/28312?key=a99e351925cf71d94b0a287935519cc4bf06a2f2&limit=4&format=pretty&shopLocation=${window.shopLocation}&url=${pageUrl}`)
    .then(resp => resp.json())
    .then(data => {
      cartRecs.value = data.data;
    })
  }

  const addItem = (data) => {
    const itemAddedToCart = (items.value || []).find(item =>
      item.variant_id === Number(data.id) &&
      (!data.properties || JSON.stringify(item.properties) === JSON.stringify(data.properties)) &&
      (!data.selling_plan 
        ? !item.selling_plan_allocation 
        : item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id === Number(data.selling_plan))
    );

    if(!itemAddedToCart){
      store.dispatch("add", data)
    }else{
      change({line: itemAddedToCart.line,quantity: itemAddedToCart.quantity + 1})
    }
  }

  const change = (data) => {
    store.dispatch("change", data);
  }

  const update = (data) => { 
    store.dispatch("update", data);
  }

  watch(cartStatus, (value) => {
    cbs.value.forEach((cb, index) => {
      cb(value);
      cbs.value.splice(index, 1);
    });
  })

  watch(totalCount, (count) => {
    const cartEl = document.querySelectorAll(".js-cart-count");
    cartEl.forEach((ele) => {
      if(count > 0) {
        ele.classList.add('active')
      }else {
        ele.classList.remove('active')
      }
    });
  })

  const toggleCart = () => {
    emit('on-toggle',false)
  }
</script>